// API service for syncing user profiles with the server
import { UserState } from '../types';

const API_BASE = ''; // Same origin

export interface ProfileResponse {
  exists: boolean;
  user?: {
    id: number;
    telegram_id: number;
    name: string;
    username?: string;
    photo_url?: string;
    about?: string;
    city?: string;
    created_at: string;
    is_verified?: boolean;
    phone_number?: string;
    referral_code?: string;
    referred_by?: string;
    referral_count?: number;
    is_premium?: boolean;
    premium_until?: string;
    average_rating?: number;
    geolocation_granted?: boolean;
    geolocation_granted_at?: string;
    drinking_preferences?: string; // Предпочтения пользователя (что обычно пьет)
    profile_analysis?: string;
  };
  ad?: {
    id: number;
    lat?: number;
    lng?: number;
    inventory: string;
    mood: string;
    description: string;
    is_active: boolean;
    last_active: string;
  };
}

export const getPhotoUrl = (photoUrl?: string): string | null => {
  if (!photoUrl) return null;
  // If it's already a full URL, return as is
  if (photoUrl.startsWith('http')) return photoUrl;
  // If it's a relative path, make it absolute
  return `${window.location.origin}${photoUrl}`;
};

export const fetchProfile = async (telegramId: number): Promise<ProfileResponse> => {
  try {
    const response = await fetch(`${API_BASE}/api/profile/${telegramId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching profile:', error);
    return { exists: false };
  }
};

export const saveProfile = async (data: {
  telegram_id: number;
  name: string;
  username?: string;
  photo_url?: string;
  photo_base64?: string;
  about?: string;
  city?: string;
  drinking_preferences?: string; // Предпочтения пользователя
  // Убраны поля объявления - они создаются отдельно через saveAd
}): Promise<{ success: boolean; user_id?: number; error?: string; message?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/api/profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.message || errorData.error || 'Failed to save profile';
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error saving profile:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { success: false, error: errorMessage };
  }
};

export const updateProfile = async (data: {
  telegram_id: number;
  name?: string;
  username?: string;
  photo_base64?: string | null; // Don't pass photo_url - server will use existing one from DB or set new one. null = delete photo
  about?: string;
  city?: string;
  drinking_preferences?: string; // Предпочтения пользователя
  profile_analysis?: string;
  delete_photo?: boolean; // Флаг для явного удаления фото
}): Promise<{ success: boolean; photo_url?: string | null; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/api/profile/${data.telegram_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update profile');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating profile:', error);
    return { success: false, error: String(error) };
  }
};

export const updateLocation = async (
  telegramId: number,
  lat: number,
  lng: number,
  grantGeolocation?: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/api/profile/${telegramId}/location`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lat, lng, grant_geolocation: grantGeolocation }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update location');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating location:', error);
    return { success: false, error: String(error) };
  }
};

export const saveAd = async (data: {
  telegram_id: number;
  inventory: string;
  mood: string;
  description?: string;
  ad_analysis?: string; // AI комментарий к предложению (отдельно от description)
  lat?: number;
  lng?: number;
  photo_url?: string;
  photos_json?: string[]; // Массив URL фото
  expires_in_hours?: number;
  meeting_date?: string;
  meeting_time?: string;
  invitedFriendIds?: number[];
}): Promise<{ success: boolean; ad_id?: number; error?: string; message?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/api/ads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.message || errorData.error || 'Failed to save ad';
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error saving ad:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { success: false, error: errorMessage };
  }
};

export const updateAd = async (data: {
  ad_id: number;
  telegram_id: number;
  inventory?: string;
  mood?: string;
  description?: string;
  ad_analysis?: string; // AI комментарий к предложению (отдельно от description)
  lat?: number;
  lng?: number;
  photo_url?: string;
  photos_json?: string[]; // Массив URL фото
  expires_in_hours?: number;
  meeting_date?: string;
  meeting_time?: string;
  invitedFriendIds?: number[];
}): Promise<{ success: boolean; error?: string; message?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/api/ads/${data.ad_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        telegram_id: data.telegram_id,
        inventory: data.inventory,
        mood: data.mood,
        description: data.description,
        ad_analysis: data.ad_analysis,
        lat: data.lat,
        lng: data.lng,
        photo_url: data.photo_url,
        photos_json: data.photos_json,
        expires_in_hours: data.expires_in_hours,
        meeting_date: data.meeting_date,
        meeting_time: data.meeting_time,
        invitedFriendIds: data.invitedFriendIds
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.message || errorData.error || 'Failed to update ad';
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating ad:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { success: false, error: errorMessage };
  }
};

export const deleteAd = async (adId: number): Promise<{ success: boolean; error?: string; message?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/api/ads/${adId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.message || errorData.error || 'Failed to delete ad';
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting ad:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { success: false, error: errorMessage };
  }
};

